<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import Auth from 'dashboard/api/auth';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useI18n } from 'vue-i18n';
import Avatar from 'next/avatar/Avatar.vue';
import SidebarProfileMenuStatus from './SidebarProfileMenuStatus.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSeparator,
  DropdownItem,
  DropdownSection,
} from 'next/dropdown-menu/base';
import CustomBrandPolicyWrapper from '../../components/CustomBrandPolicyWrapper.vue';

defineProps({
  bodyClass: {
    type: String,
    default: 'bottom-12 z-50 mb-2 w-80 ltr:left-0 rtl:right-0',
  },
  compact: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'close',
  'openKeyShortcutModal',
  'showCreateAccountModal',
]);

defineOptions({
  inheritAttrs: false,
});

const { t } = useI18n();
const { accountId: activeAccountId, currentAccount } = useAccount();

const currentUser = useMapGetter('getCurrentUser');
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const accountId = useMapGetter('getCurrentAccountId');
const globalConfig = useMapGetter('globalConfig/get');
const userAccounts = useMapGetter('getUserAccounts');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

// --- Estado SIP ---
const sipRegistered = ref(false);
const sipConfigured = ref(false);
const sipExtension = ref('');
const voiceDialerLabel = computed(() => t('SIDEBAR_ITEMS.VOICE_CALL'));

const sipStatusLabel = computed(() => {
  if (sipRegistered.value) return `SIP · ${sipExtension.value}`;
  if (sipConfigured.value) return t('SIDEBAR_ITEMS.SIP_CONNECTING');
  return t('SIDEBAR_ITEMS.SIP_CONFIGURE');
});

const sipDotClass = computed(() => {
  if (sipRegistered.value) return 'bg-n-teal-9';
  if (sipConfigured.value) return 'bg-n-amber-9';
  return 'bg-n-slate-7';
});

const onSipRegistered = e => {
  sipRegistered.value = true;
  sipConfigured.value = true;
  sipExtension.value = e.detail?.userInfo?.extension || sipExtension.value;
};
const onSipUnregistered = () => {
  sipRegistered.value = false;
};
const onSipNotConfigured = () => {
  sipRegistered.value = false;
  sipConfigured.value = false;
};

const openSipConfig = () => {
  window.dispatchEvent(new CustomEvent('voice:openSipConfig'));
};

const openVoiceDialer = () => {
  window.dispatchEvent(new CustomEvent('voice:openDialer'));
};

onMounted(() => {
  try {
    const cu = currentUser.value;
    const aid = accountId.value;
    if (cu?.id && aid) {
      const stored = JSON.parse(
        localStorage.getItem(`vc_sip_${aid}_${cu.id}`) || 'null'
      );
      if (stored?.extension && stored?.password) {
        sipConfigured.value = true;
        sipExtension.value = stored.extension;
      }
    }
  } catch {
    // no action needed
  }
  if (window.voiceSIPClient?.isRegistered) sipRegistered.value = true;
  window.addEventListener('sip:registered', onSipRegistered);
  window.addEventListener('sip:unregistered', onSipUnregistered);
  window.addEventListener('voice:sipNotConfigured', onSipNotConfigured);
});

onUnmounted(() => {
  window.removeEventListener('sip:registered', onSipRegistered);
  window.removeEventListener('sip:unregistered', onSipUnregistered);
  window.removeEventListener('voice:sipNotConfigured', onSipNotConfigured);
});

const showChatSupport = computed(() => {
  return (
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.CONTACT_CHATWOOT_SUPPORT_TEAM
    ) && globalConfig.value.chatwootInboxToken
  );
});

const menuItems = computed(() => {
  return [
    {
      show: showChatSupport.value,
      showOnCustomBrandedInstance: false,
      label: t('SIDEBAR_ITEMS.CONTACT_SUPPORT'),
      icon: 'i-lucide-life-buoy',
      click: () => {
        window.$chatwoot.toggle();
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.KEYBOARD_SHORTCUTS'),
      icon: 'i-lucide-keyboard',
      click: () => {
        emit('openKeyShortcutModal');
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.PROFILE_SETTINGS'),
      icon: 'i-lucide-user-pen',
      link: { name: 'profile_settings_index' },
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.APPEARANCE'),
      icon: 'i-lucide-palette',
      link: { name: 'profile_settings_index' },
    },
    {
      show: true,
      showOnCustomBrandedInstance: false,
      label: t('SIDEBAR_ITEMS.DOCS'),
      icon: 'i-lucide-book',
      link: 'https://www.chatwoot.com/hc/user-guide/en',
      nativeLink: true,
      target: '_blank',
    },
    {
      show: true,
      showOnCustomBrandedInstance: false,
      label: t('SIDEBAR_ITEMS.CHANGELOG'),
      icon: 'i-lucide-scroll-text',
      link: 'https://www.chatwoot.com/changelog/',
      nativeLink: true,
      target: '_blank',
    },
    {
      show: false,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.SUPER_ADMIN_CONSOLE'),
      icon: 'i-lucide-castle',
      link: '/super_admin',
      nativeLink: true,
      target: '_blank',
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.LOGOUT'),
      icon: 'i-lucide-power',
      click: Auth.logout,
    },
  ];
});

const allowedMenuItems = computed(() => {
  return menuItems.value.filter(item => item.show);
});

const showAccountSwitcher = computed(
  () => userAccounts.value.length > 1 && currentAccount.value?.name
);

const sortedCurrentUserAccounts = computed(() => {
  return [...(currentUser.value.accounts || [])].sort((a, b) =>
    a.name.localeCompare(b.name)
  );
});

const onChangeAccount = newId => {
  window.location.href = `/app/accounts/${newId}/dashboard`;
};

const roleLabel = role => {
  const normalizedRole = String(role || '').toUpperCase();
  return t(`SIDEBAR_ITEMS.ACCOUNT_ROLES.${normalizedRole}`, role || '');
};

const emitNewAccount = () => {
  emit('showCreateAccountModal');
};

const displayName = computed(() => {
  return (
    currentUser.value.available_name || currentUser.value.name || 'Usuario'
  );
});
</script>

<template>
  <DropdownContainer class="relative w-full min-w-0" @close="emit('close')">
    <template #trigger="{ toggle, isOpen }">
      <button
        class="flex gap-2 items-center p-1 w-full text-left rounded-lg cursor-pointer hover:bg-n-alpha-1"
        :aria-label="displayName"
        :title="displayName"
        :class="[
          { 'bg-n-alpha-1': isOpen },
          compact ? 'justify-center !p-0 size-12' : '',
        ]"
        @click="toggle"
      >
        <div class="relative flex-shrink-0">
          <Avatar
            :size="32"
            :name="displayName"
            :src="currentUser.avatar_url"
            :status="currentUserAvailability"
            rounded-full
          />
          <!-- Indicador SIP (punto pequeño sobre el avatar) -->
          <span
            class="absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full border-2 border-n-solid-2"
            :class="sipDotClass"
          />
        </div>
        <div v-if="!compact" class="min-w-0">
          <div class="text-sm font-medium leading-4 truncate text-n-slate-12">
            {{ displayName }}
          </div>
          <div class="text-xs truncate text-n-slate-11">
            {{ currentUser.email }}
          </div>
        </div>
      </button>
    </template>
    <DropdownBody :class="bodyClass">
      <DropdownSection>
        <div class="flex items-center gap-3 px-3 py-2">
          <Avatar
            :size="40"
            :name="displayName"
            :src="currentUser.avatar_url"
            :status="currentUserAvailability"
            rounded-full
          />
          <div class="min-w-0">
            <div
              class="text-sm font-semibold leading-5 truncate text-n-slate-12"
            >
              {{ displayName }}
            </div>
            <div class="text-xs truncate text-n-slate-11">
              {{ currentUser.email }}
            </div>
          </div>
        </div>
      </DropdownSection>
      <DropdownSeparator />
      <DropdownSection :title="t('SIDEBAR_ITEMS.SWITCH_ACCOUNT')">
        <div class="tic-profile-account-list">
          <button
            v-for="account in sortedCurrentUserAccounts"
            :key="account.id"
            type="button"
            class="tic-profile-account-button"
            :class="{ 'is-active': account.id === activeAccountId }"
            @click="onChangeAccount(account.id)"
          >
            <span class="truncate">{{ account.name }}</span>
            <small class="truncate">
              {{
                account.custom_role_id && account.custom_role
                  ? account.custom_role.name
                  : roleLabel(account.role)
              }}
            </small>
            <span
              v-if="account.id === activeAccountId"
              class="i-lucide-check size-4"
            />
          </button>
          <p v-if="!showAccountSwitcher" class="tic-profile-account-single">
            {{ currentAccount?.name }}
          </p>
          <button
            v-if="globalConfig.createNewAccountFromDashboard"
            type="button"
            class="tic-profile-account-new"
            @click="emitNewAccount"
          >
            {{ t('CREATE_ACCOUNT.NEW_ACCOUNT') }}
          </button>
        </div>
      </DropdownSection>
      <DropdownSeparator />
      <SidebarProfileMenuStatus />
      <DropdownSeparator />
      <!-- Elemento SIP -->
      <DropdownSection>
        <button
          type="button"
          class="flex items-center w-full gap-2.5 px-3 py-2 rounded-lg text-sm cursor-pointer text-left hover:bg-n-alpha-2"
          @click="openVoiceDialer"
        >
          <span class="i-ri-phone-fill size-4 text-n-slate-10 flex-shrink-0" />
          <span class="flex-1 truncate text-n-slate-12 font-medium">
            {{ voiceDialerLabel }}
          </span>
          <span
            class="size-2 rounded-full flex-shrink-0"
            :class="sipDotClass"
          />
        </button>
        <button
          type="button"
          class="flex items-center w-full gap-2.5 px-3 py-2 rounded-lg text-sm cursor-pointer text-left hover:bg-n-alpha-2"
          @click="openSipConfig"
        >
          <span
            class="size-2 rounded-full flex-shrink-0"
            :class="sipDotClass"
          />
          <span
            class="flex-1 truncate"
            :class="
              sipRegistered ? 'text-n-teal-11 font-medium' : 'text-n-slate-11'
            "
          >
            {{ sipStatusLabel }}
          </span>
          <span
            class="i-lucide-settings size-3.5 text-n-slate-9 flex-shrink-0"
          />
        </button>
      </DropdownSection>
      <DropdownSeparator />
      <template v-for="item in allowedMenuItems" :key="item.label">
        <CustomBrandPolicyWrapper
          :show-on-custom-branded-instance="item.showOnCustomBrandedInstance"
        >
          <DropdownItem v-if="item.show" v-bind="item" />
        </CustomBrandPolicyWrapper>
      </template>
    </DropdownBody>
  </DropdownContainer>
</template>

<style scoped lang="scss">
.tic-profile-account-list {
  display: grid;
  gap: 0.4rem;
  max-height: 14rem;
  overflow-y: auto;
  padding: 0.1rem;
}

.tic-profile-account-button,
.tic-profile-account-new {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.25rem 0.55rem;
  width: 100%;
  padding: 0.65rem 0.75rem;
  border: 1px solid rgba(148, 234, 255, 0.16);
  border-radius: 0.75rem;
  background: #071827;
  color: rgba(248, 250, 252, 0.94);
  text-align: left;

  small {
    grid-column: 1;
    color: rgba(191, 219, 254, 0.72);
    font-size: 0.72rem;
    text-transform: capitalize;
  }

  &:hover,
  &.is-active {
    border-color: rgba(38, 217, 247, 0.48);
    background: #0b2735;
  }
}

.tic-profile-account-new {
  display: block;
  color: #26d9f7;
  font-weight: 800;
}

.tic-profile-account-single {
  margin: 0;
  padding: 0.55rem 0.75rem;
  color: rgba(248, 250, 252, 0.92);
  font-weight: 800;
}

:global(body:not(.dark)) {
  .tic-profile-account-button,
  .tic-profile-account-new {
    background: #ffffff;
    border-color: rgba(15, 23, 42, 0.08);
    color: #111827;

    small {
      color: #53657f;
    }

    &:hover,
    &.is-active {
      background: #e9fbff;
      border-color: rgba(38, 217, 247, 0.45);
    }
  }

  .tic-profile-account-single {
    color: #111827;
  }
}
</style>
